import "@/lib/request-id";
import "@/index.css";
import "dayjs/locale/zh-tw"; // import locale
import "./i18n"; // import i18n configuration

import { ConfigProvider, notification } from "antd";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";

import AccountPayableCalender from "./pages/AccountPayableCalender";
import AccountPayableMainPage from "@/pages/AccountPayableMainPage";
import AccountReceivableMainPage from "@/pages/AccountReceivableMainPage";
import EditAccountPayablePage from "@/pages/EditAccountPayablePage";
import EditAccountReceivablePage from "@/pages/EditAccountReceivablePage";
import AddCompanyListPage from "@/pages/AddCompanyListPage";
import AddCoopListPage from "@/pages/AddCoopListPage";
import AddDepartmentListPage from "@/pages/AddDepartmentListPage";
import AddDesignBOM from "@/pages/AddDesignBOM";
import AddEmployeeListPage from "@/pages/AddEmployeeListPage";
import AddInventoryCreatedListPage from "@/pages/AddInventoryCreated";
import AddInventoryDoneListPage from "@/pages/AddInventoryDone";
import AddProductListPage from "@/pages/AddProductListPage";
import AddPurchaseOrder from "@/pages/AddPurchaseOrder";
import AddQuotationListPage from "@/pages/AddQuotationListPage";
import AddSteelAssemblyBOM from "@/pages/AddSteelAssemblyBOM";
import AddSteelBoltPage from "@/pages/AddSteelBoltPage";
import AddStockRequestCreated from "@/pages/AddStockRequestCreated";
import AddStockRequestDone from "@/pages/AddStockRequestDone";
import AddWarehouseListPage from "@/pages/AddWarehouseListPage";
import App from "@/App";
import BankMainPage from "@/pages/BankMainPage";
import { BaseContextProvider } from "@/utility/state/contextAPI";
import CompanyCalendarPage from "@/pages/CompanyCalendarPage";
import CompanyInfoPage from "@/pages/CompanyInfoPage";
import CompanyListPage from "@/pages/CompanyListPage";
import CoopManagement from "@/pages/CoopManagement";
import CoopPage from "@/pages/CoopPage";
import CoopPendingManagement from "@/pages/CoopPendingManagement";
import CoopReviewedManagement from "@/pages/CoopReviewedManagement";
import DepartmentManagement from "@/pages/DepartmentManagement";
import DepartmentPage from "@/pages/DepartmentPage";
import DesignBOM from "@/pages/DesignBOM";
import EditCompanyListPage from "@/pages/EditCompanyListPage";
import EditCoopPage from "@/pages/EditCoopPage";
import EditDepartmentPage from "@/pages/EditDepartmentPage";
import EditEmployeePage from "@/pages/EditEmployeePage";
import EditInventoryCreatedPage from "@/pages/EditInventoryCreatedPage";
import EditInventoryDonePage from "@/pages/EditInventoryDonePage";
import EditProductListPage from "@/pages/EditProductListPage";
import EditPurchaseOrderPage from "@/pages/EditPurchaseOrderPage";
import EditQuotationPage from "@/pages/EditQuotationPage";
import EditStockRequestCreatedPage from "@/pages/EditStockRequestCreatedPage";
import EditStockRequestDonePage from "@/pages/EditStockRequestDonePage";
import EditWarehouseListPage from "@/pages/EditWarehouseListPage";
import EmployeeManagement from "@/pages/EmployeeManagement";
import EmployeePage from "@/pages/EmployeePage";
import EmployeesPage from "@/pages/employees";
import GoogleOAuthCallbackPage from "@/pages/GoogleOAuthCallbackPage";
import HRMainPage from "@/pages/HRMainPage";
import InventoryCreatedManagement from "@/pages/InventoryCreatedManagement";
import InventoryCreatedPage from "@/pages/InventoryCreatedPage";
import InventoryDoneManagement from "@/pages/InventoryDoneManagement";
import InventoryDonePage from "./pages/InventoryDonePage";
import ItemEditorPage from "@/pages/ItemEditor";
import LoginPage from "@/pages/LoginPage";
import { MantineProvider } from "@mantine/core";
import PhoneVerificationPage from "@/pages/PhoneVerificationPage";
import ProductListPage from "@/pages/ProductListPage";
import ProductManagement from "@/pages/ProductManagement";
import ProductionPlan from "@/pages/ProductionPlan";
import DocumentAnnotatePage from "@/pages/DocumentAnnotatePage";
import PurchaseOrderApprovedManagement from "@/pages/PurchaseOrderApprovedManagement";
import PurchaseOrderCompletedManagement from "@/pages/PurchaseOrderCompletedManagement";
import PurchaseOrderDraftManagement from "@/pages/PurchaseOrderDraftManagement";
import PurchaseOrderPage from "@/pages/PurchaseOrderPage";
import PurchaseOrderUnderReviewManagement from "@/pages/PurchaseOrderUnderReviewManagement";
import RfqListPage from "@/pages/RfqListPage";
import RfqPage from "@/pages/RfqPage";
import QuotationManagement from "@/pages/QuotationManagement";
import QuotationPage from "@/pages/QuotationPage";
import SignUpPage from "@/pages/SignUpPage";
import SteelAssemblyBOM from "@/pages/SteelAssemblyBOM";
import SteelBoltPage from "@/pages/SteelBoltPage";
import StockHistoryPage from "@/pages/StockHistoryPage";
import StockRequestCreatedManagement from "@/pages/StockRequestCreatedManagementPage";
import StockRequestCreatedPage from "@/pages/StockRequestCreatedPage";
import StockRequestDoneManagement from "@/pages/StockRequestDoneManagementPage";
import StockRequestDonePage from "@/pages/StockRequestDonePage";
import StorageManagement from "@/pages/WarehouseManagement";
import StoragePage from "@/pages/WarehousePage";
import WarehouseManagementSystemPage from "@/pages/warehouseManagementSystem/WarehouseManagementSystemPage";
import WarRoomPage from "@/pages/WarRoomPage";
import { StrictMode } from "react";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { authClient } from "@/lib/auth-clients";
import { reloadForStaleChunk } from "@/lib/lazy-with-reload";
import { createRoot } from "react-dom/client";
import customParseFormat from "dayjs/plugin/customParseFormat";
//import * as dayjs from "dayjs";
import dayjs from "dayjs";
import locale from "antd/locale/zh_TW";
import localeData from "dayjs/plugin/localeData";
import updateLocale from "dayjs/plugin/updateLocale";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";
import weekday from "dayjs/plugin/weekday";

notification.config({ duration: 1.5 });

window.addEventListener("vite:preloadError", (event) => {
  if (reloadForStaleChunk()) event.preventDefault();
});

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

dayjs.extend(updateLocale);
dayjs.updateLocale("zh-tw", {
  weekStart: 0,
});

dayjs.locale("zh-tw");

function RootRedirect() {
  const { data: session, isPending } = authClient.useSession();
  if (isPending) {
    return null;
  }

  return session ? (
    <Navigate to="/companyListPage" replace />
  ) : (
    <Navigate to="/login" replace />
  );
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    children: [
      { index: true, element: <RootRedirect /> },
      { path: "login", element: <LoginPage /> },
      {
        path: "auth/:providerName/callback",
        element: <GoogleOAuthCallbackPage />,
      },
      { path: "phoneVerification", element: <PhoneVerificationPage /> },
      { path: "signUp", element: <SignUpPage /> },
      { path: "companyListPage", element: <CompanyListPage /> },
      { path: "addCompanyListPage", element: <AddCompanyListPage /> },
      {
        path: "hrMainPage",
        element: <HRMainPage />,
        children: [
          {
            path: "calendar",
            element: <CompanyCalendarPage />,
          },
          {
            path: "companyInfo/",
            element: <CompanyInfoPage />,
          },
          {
            path: "editCompanyInfo",
            element: <EditCompanyListPage />,
          },
          {
            path: "editEmployee/:id",
            element: <EditEmployeePage />,
          },
          {
            path: "employee/:id",
            element: <EmployeePage />,
          },
          {
            path: "employee/",
            element: <EmployeeManagement />,
          },
          {
            path: "employees",
            element: <EmployeesPage />,
          },
          {
            path: "warRoom",
            element: <WarRoomPage />,
          },
          {
            path: "addEmployee/",
            element: <AddEmployeeListPage />,
          },
          {
            path: "department",
            element: <DepartmentManagement />,
          },
          {
            path: "department/:id",
            element: <DepartmentPage />,
          },
          {
            path: "editDepartment/:id",
            element: <EditDepartmentPage />,
          },
          {
            path: "addDepartment/",
            element: <AddDepartmentListPage />,
          },
          {
            path: "coop",
            element: <CoopManagement />,
          },
          {
            path: "coop/:id",
            element: <CoopPage />,
          },
          {
            path: "editCoop/:id",
            element: <EditCoopPage />,
          },
          {
            path: "addCoop",
            element: <AddCoopListPage />,
          },
          {
            path: "coopPending",
            element: <CoopPendingManagement />,
          },
          {
            path: "coopReviewed",
            element: <CoopReviewedManagement />,
          },
          {
            path: "product",
            element: <ProductManagement />,
          },
          {
            path: "addProduct",
            element: <AddProductListPage />,
          },
          {
            path: "product/:id",
            element: <ProductListPage />,
          },
          {
            path: "editProduct/:id",
            element: <EditProductListPage />,
          },
          {
            path: "itemEditor",
            element: <ItemEditorPage />,
          },

          { path: "inventoryCreated", element: <InventoryCreatedManagement /> },
          { path: "inventoryCreated/:id", element: <InventoryCreatedPage /> },

          {
            path: "editInventoryCreated/:id",
            element: <EditInventoryCreatedPage />,
          },

          {
            path: "addInventoryCreated",
            element: <AddInventoryCreatedListPage />,
          },

          { path: "inventoryDone", element: <InventoryDoneManagement /> },
          { path: "inventoryDone/:id", element: <InventoryDonePage /> },

          { path: "editInventoryDone/:id", element: <EditInventoryDonePage /> },

          { path: "addInventoryDone", element: <AddInventoryDoneListPage /> },

          {
            path: "stockRequestCreated",
            element: <StockRequestCreatedManagement />,
          },
          {
            path: "addStockRequestCreated",
            element: <AddStockRequestCreated />,
          },
          {
            path: "stockRequestCreated/:id",
            element: <StockRequestCreatedPage />,
          },
          {
            path: "editStockRequestCreated/:id",
            element: <EditStockRequestCreatedPage />,
          },

          {
            path: "stockRequestDone",
            element: <StockRequestDoneManagement />,
          },
          {
            path: "addStockRequestDone",
            element: <AddStockRequestDone />,
          },
          { path: "stockRequestDone/:id", element: <StockRequestDonePage /> },
          {
            path: "editStockRequestDone/:id",
            element: <EditStockRequestDonePage />,
          },
          {
            path: "warehouse/",
            element: <StorageManagement />,
          },
          {
            path: "stockHistory",
            element: <StockHistoryPage />,
          },
          {
            path: "addWarehouse",
            element: <AddWarehouseListPage />,
          },
          {
            path: "warehouse/:id",
            element: <StoragePage />,
          },
          {
            path: "editWarehouse/:id",
            element: <EditWarehouseListPage />,
          },
          {
            path: "accountPayable",
            element: <AccountPayableMainPage />,
          },
          {
            path: "editAccountPayable/:id",
            element: <EditAccountPayablePage />,
          },
          {
            path: "accountReceivable",
            element: <AccountReceivableMainPage />,
          },
          {
            path: "editAccountReceivable/:id",
            element: <EditAccountReceivablePage />,
          },
          {
            path: "bank",
            element: <BankMainPage />,
          },
          {
            path: "addPurchaseOrder",
            element: <AddPurchaseOrder />,
          },
          {
            path: "purchaseOrderDraft",
            element: <PurchaseOrderDraftManagement />,
          },
          {
            path: "purchaseOrderUnderReview",
            element: <PurchaseOrderUnderReviewManagement />,
          },
          {
            path: "purchaseOrderCompleted",
            element: <PurchaseOrderCompletedManagement />,
          },
          {
            path: "purchaseOrderApproved",
            element: <PurchaseOrderApprovedManagement />,
          },
          {
            path: "purchaseOrder/:id",
            element: <PurchaseOrderPage />,
          },
          {
            path: "editPurchaseOrder/:id",
            element: <EditPurchaseOrderPage />,
          },
          {
            path: "rfq",
            element: <Navigate to="/hrMainPage/rfqDraft" replace />,
          },
          {
            path: "rfqDraft",
            element: <RfqListPage status="inquiring" />,
          },
          {
            path: "rfqUnderReview",
            element: <RfqListPage status="underReview" />,
          },
          {
            path: "rfqCompleted",
            element: <RfqListPage status="completed" />,
          },
          {
            path: "rfq/:id",
            element: <RfqPage />,
          },
          {
            path: "accountPayableCalender",
            element: <AccountPayableCalender />,
          },
          {
            path: "projectQuotation",
            element: <QuotationManagement />,
          },
          {
            path: "projectQuotationArchived",
            element: <QuotationManagement status="close" />,
          },
          {
            path: "addProjectQuotation",
            element: <AddQuotationListPage />,
          },
          {
            path: "SteelAssemblyBOM/:id",
            element: <SteelAssemblyBOM />,
          },
          {
            path: "addSteelAssemblyBOM/:id",
            element: <AddSteelAssemblyBOM />,
          },
          {
            path: "projectQuotation/:id",
            element: <QuotationPage />,
          },
          {
            path: "EditQuotation/:id",
            element: <EditQuotationPage />,
          },
          {
            path: "steelBoltBOM/:id",
            element: <SteelBoltPage />,
          },
          {
            path: "addSteelBoltBOM/:id",
            element: <AddSteelBoltPage />,
          },
          {
            path: "addDesignBOM/:id",
            element: <AddDesignBOM />,
          },
          {
            path: "designBOM/:id",
            element: <DesignBOM />,
          },

          // 草稿: 'hrMainPage/purchaseOrder/draft',
          // 審核中: 'hrMainPage/purchaseOrder/underReview',
          // 已審核: 'hrMainPage/purchaseOrder/approved',
          // 已完成: 'hrMainPage/purchaseOrder/completed',
        ],
      },
      {
        path: "hrMainPage/warehouseSystem",
        element: <WarehouseManagementSystemPage />,
      },
      {
        path: "hrMainPage/warehouseSystem/:id",
        element: <WarehouseManagementSystemPage />,
      },
      {
        path: "productionPlan/:id",
        element: <ProductionPlan />,
      },
      {
        path: "documentAnnotate",
        element: <DocumentAnnotatePage />,
      },
      {
        path: "*",
        element: <RootRedirect />,
      },
    ],
  },
]);

const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);
  root.render(
    <StrictMode>
      <ConfigProvider locale={locale}>
        <BaseContextProvider>
          <MantineProvider>
            <RouterProvider router={router} />
          </MantineProvider>
        </BaseContextProvider>
      </ConfigProvider>
    </StrictMode>
  );
}
